/**
 * FixedAssetsTab — Activos Fijos
 */
import { todayISO } from '../../../lib/utils'
import { formatMoneyForTenant } from '@/lib/locale'
import { useState, useEffect } from 'react'
import { EntityAutocomplete } from '@/components/EntityAutocomplete'
import { Plus, Loader2, Search, Building2, XCircle } from 'lucide-react'
import { DataTable } from '@/components/ds/DataTable'
import {
  useFixedAssets, useCreateFixedAsset, useDisposeFixedAsset,
  useAccounts, useCostCenters,
  type FixedAssetItem, type ChartOfAccount,
} from '../hooks/useAccounting'
import { KebabMenu, type KebabMenuItem } from '@/components/KebabMenu'
import { Modal } from '@/components/ds/Modal'

const STATUS_LABELS: Record<string, string> = {
  active: 'Activo',
  disposed: 'Dado de Baja',
  fully_depreciated: 'Depreciado',
}

const STATUS_COLORS: Record<string, string> = {
  active: 'bg-success-surface text-success-content',
  disposed: 'bg-danger-surface text-danger-content',
  fully_depreciated: 'bg-warning-surface text-warning-content',
}

const CATEGORY_LABELS: Record<string, string> = {
  machinery: 'Maquinaria',
  vehicles: 'Vehiculos',
  equipment: 'Equipos',
  furniture: 'Mobiliario',
  buildings: 'Edificios',
  tools: 'Herramientas',
  other: 'Otro',
}

const CATEGORY_COLORS: Record<string, string> = {
  machinery: 'bg-primary-50 text-primary-hover',
  vehicles: 'bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300',
  equipment: 'bg-cyan-50 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300',
  furniture: 'bg-warning-surface text-warning-content',
  buildings: 'bg-surface-muted text-content-muted',
  tools: 'bg-orange-50 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300',
  other: 'bg-surface-muted text-content-muted',
}

const DEPRECIATION_METHODS: Record<string, string> = {
  straight_line: 'Línea Recta',
  accelerated: 'Acelerada',
}

function fmtCLP(value: string | number | undefined) {
  if (value === undefined || value === null) return '--'
  const num = typeof value === 'string' ? parseFloat(value) : value
  if (isNaN(num)) return '--'
  return formatMoneyForTenant(num)
}

// ─── Dispose Modal ──────────────────────────────────────────────────────────

interface DisposeModalProps {
  asset: FixedAssetItem
  onClose: () => void
}

function DisposeModal({ asset, onClose }: DisposeModalProps) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [onClose])

  const [disposedDate, setDisposedDate] = useState(todayISO())
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)
  const disposeMut = useDisposeFixedAsset()

  const handleDispose = () => {
    setError(null)
    disposeMut.mutate(
      { id: asset.id, disposed_date: disposedDate, reason },
      {
        onSuccess: onClose,
        onError: (e: unknown) => {
          const err = e as { response?: { data?: { error?: { message?: string } } } }
          setError(err.response?.data?.error?.message ?? 'Error al dar de baja')
        },
      },
    )
  }

  return (
    <Modal open onClose={onClose} title="Dar de Baja Activo" size="lg"
      actions={<>
        <button onClick={onClose} className="px-4 py-2 text-sm text-content-muted border border-divider rounded-lg hover:bg-soft">
          Cancelar
        </button>
        <button
          onClick={handleDispose}
          disabled={disposeMut.isPending || !disposedDate}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-danger-fill text-white rounded-lg hover:bg-danger-fill-hover disabled:opacity-50"
        >
          {disposeMut.isPending && <Loader2 size={14} className="animate-spin" />}
          Confirmar Baja
        </button>
      </>}
    >
      <div className="space-y-4">
        <div className="bg-glass-3 rounded-lg p-3">
          <p className="text-sm text-content-muted"><span className="font-medium">{asset.code}</span> - {asset.name}</p>
          <p className="text-xs text-content-subtle mt-1">Valor libro: {fmtCLP(asset.book_value)}</p>
        </div>
        <div>
          <label htmlFor="fixedassetstab-fecha-de-baja" className="block text-sm font-medium text-content mb-1">Fecha de Baja *</label>
          <input id="fixedassetstab-fecha-de-baja" type="date" value={disposedDate} onChange={e => setDisposedDate(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-input-border rounded-lg bg-input-bg text-content focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary-200" />
        </div>
        <div>
          <label htmlFor="fixedassetstab-razon" className="block text-sm font-medium text-content mb-1">Razon</label>
          <textarea id="fixedassetstab-razon" value={reason} onChange={e => setReason(e.target.value)} rows={3}
            placeholder="Motivo de la baja (opcional)"
            className="w-full px-3 py-2 text-sm border border-input-border rounded-lg bg-input-bg text-content focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary-200 resize-none" />
        </div>
        {error && (
          <div className="bg-danger-surface border border-danger-border rounded-lg px-3 py-2 text-sm text-danger-content">{error}</div>
        )}
      </div>
    </Modal>
  )
}

// ─── Create Modal ───────────────────────────────────────────────────────────

interface CreateAssetFormProps {
  accounts: ChartOfAccount[]
  onClose: () => void
}

function CreateAssetForm({ accounts, onClose }: CreateAssetFormProps) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [onClose])

  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [category, setCategory] = useState('equipment')
  const [acquisitionDate, setAcquisitionDate] = useState(todayISO())
  const [acquisitionCost, setAcquisitionCost] = useState('')
  const [residualValue, setResidualValue] = useState('0')
  const [usefulLifeMonths, setUsefulLifeMonths] = useState('60')
  const [depreciationMethod, setDepreciationMethod] = useState('straight_line')
  const [accountAsset, setAccountAsset] = useState('')
  const [accountDepreciation, setAccountDepreciation] = useState('')
  const [accountAccumulated, setAccountAccumulated] = useState('')
  const [costCenter, setCostCenter] = useState('')
  const [location, setLocation] = useState('')
  const [error, setError] = useState<string | null>(null)

  const create = useCreateFixedAsset()
  const { data: costCentersData } = useCostCenters({ is_active: 'true' })
  const costCenterOptions = (costCentersData?.cost_centers ?? []).map(c => ({ id: c.id, label: `${c.code} — ${c.name}` }))

  const imputableAccounts = accounts.filter(a => a.is_imputable && a.is_active)

  // Las tres cuentas son obligatorias: sin ellas el activo no puede depreciarse.
  const faltaDato = !code || !name || !acquisitionCost || !acquisitionDate
    || !accountAsset || !accountDepreciation || !accountAccumulated

  const handleSave = () => {
    if (faltaDato) return
    setError(null)
    create.mutate(
      {
        code, name, description, category,
        acquisition_date: acquisitionDate,
        acquisition_cost: acquisitionCost,
        salvage_value: residualValue || '0',
        useful_life_months: parseInt(usefulLifeMonths) || 60,
        depreciation_method: depreciationMethod,
        asset_account: accountAsset,
        depreciation_account: accountDepreciation,
        accumulated_account: accountAccumulated,
        cost_center: costCenter || null,
        location,
      },
      {
        onSuccess: onClose,
        onError: (e: unknown) => {
          const err = e as { response?: { data?: { error?: { message?: string; details?: Record<string, string[]> } } } }
          const detalle = Object.values(err.response?.data?.error?.details ?? {}).flat().join(' ')
          setError(detalle || err.response?.data?.error?.message || 'Error al guardar')
        },
      },
    )
  }

  return (
    <Modal open onClose={onClose} title="Nuevo Activo Fijo" size="2xl"
      actions={<>
        <button onClick={onClose} className="px-4 py-2 text-sm text-content-muted border border-divider rounded-lg hover:bg-soft">
          Cancelar
        </button>
        <button
          onClick={handleSave}
          disabled={create.isPending || faltaDato}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-primary text-on-primary rounded-lg hover:bg-primary-hover disabled:opacity-50"
        >
          {create.isPending && <Loader2 size={14} className="animate-spin" />}
          Crear Activo
        </button>
      </>}
    >
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label htmlFor="fixedassetstab-codigo" className="block text-sm font-medium text-content mb-1">Codigo *</label>
            <input id="fixedassetstab-codigo" value={code} onChange={e => setCode(e.target.value)} placeholder="ej. AF-001"
              className="w-full px-3 py-2 text-sm border border-input-border rounded-lg bg-input-bg text-content focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary-200" />
          </div>
          <div>
            <label htmlFor="fixedassetstab-categoria" className="block text-sm font-medium text-content mb-1">Categoria</label>
            <select id="fixedassetstab-categoria" value={category} onChange={e => setCategory(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-input-border rounded-lg bg-input-bg text-content focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary-200">
              {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>
        </div>
        <div>
          <label htmlFor="fixedassetstab-nombre" className="block text-sm font-medium text-content mb-1">Nombre *</label>
          <input id="fixedassetstab-nombre" value={name} onChange={e => setName(e.target.value)} placeholder="Nombre del activo"
            className="w-full px-3 py-2 text-sm border border-input-border rounded-lg bg-input-bg text-content focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary-200" />
        </div>
        <div>
          <label htmlFor="fixedassetstab-descripcion" className="block text-sm font-medium text-content mb-1">Descripcion</label>
          <textarea id="fixedassetstab-descripcion" value={description} onChange={e => setDescription(e.target.value)} rows={2}
            placeholder="Descripción del activo (opcional)"
            className="w-full px-3 py-2 text-sm border border-input-border rounded-lg bg-input-bg text-content focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary-200 resize-none" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label htmlFor="fixedassetstab-fecha-adquisicion" className="block text-sm font-medium text-content mb-1">Fecha Adquisicion *</label>
            <input id="fixedassetstab-fecha-adquisicion" type="date" value={acquisitionDate} onChange={e => setAcquisitionDate(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-input-border rounded-lg bg-input-bg text-content focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary-200" />
          </div>
          <div>
            <label htmlFor="fixedassetstab-costo-adquisicion" className="block text-sm font-medium text-content mb-1">Costo Adquisicion *</label>
            <input id="fixedassetstab-costo-adquisicion" type="number" value={acquisitionCost} onChange={e => setAcquisitionCost(e.target.value)} placeholder="0"
              className="w-full px-3 py-2 text-sm border border-input-border rounded-lg bg-input-bg text-content focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary-200" />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label htmlFor="fixedassetstab-valor-residual" className="block text-sm font-medium text-content mb-1">Valor Residual</label>
            <input id="fixedassetstab-valor-residual" type="number" value={residualValue} onChange={e => setResidualValue(e.target.value)} placeholder="0"
              className="w-full px-3 py-2 text-sm border border-input-border rounded-lg bg-input-bg text-content focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary-200" />
          </div>
          <div>
            <label htmlFor="fixedassetstab-vida-util-meses" className="block text-sm font-medium text-content mb-1">Vida Util (meses)</label>
            <input id="fixedassetstab-vida-util-meses" type="number" value={usefulLifeMonths} onChange={e => setUsefulLifeMonths(e.target.value)} placeholder="60"
              className="w-full px-3 py-2 text-sm border border-input-border rounded-lg bg-input-bg text-content focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary-200" />
          </div>
          <div>
            <label htmlFor="fixedassetstab-metodo-depreciacion" className="block text-sm font-medium text-content mb-1">Metodo Depreciacion</label>
            <select id="fixedassetstab-metodo-depreciacion" value={depreciationMethod} onChange={e => setDepreciationMethod(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-input-border rounded-lg bg-input-bg text-content focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary-200">
              {Object.entries(DEPRECIATION_METHODS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label htmlFor="fixedassetstab-cuenta-activo" className="block text-sm font-medium text-content mb-1">Cuenta Activo *</label>
            <EntityAutocomplete id="fixedassetstab-cuenta-activo"
              options={imputableAccounts.map(a => ({ id: a.id, label: `${a.code} — ${a.name}` }))}
              value={accountAsset} onChange={setAccountAsset}
              placeholder="Seleccionar" allowClear />
          </div>
          <div>
            <label htmlFor="fixedassetstab-cuenta-depreciacion" className="block text-sm font-medium text-content mb-1">Cuenta Depreciacion *</label>
            <EntityAutocomplete id="fixedassetstab-cuenta-depreciacion"
              options={imputableAccounts.map(a => ({ id: a.id, label: `${a.code} — ${a.name}` }))}
              value={accountDepreciation} onChange={setAccountDepreciation}
              placeholder="Seleccionar" allowClear />
          </div>
          <div>
            <label htmlFor="fixedassetstab-cuenta-acumulada" className="block text-sm font-medium text-content mb-1">Cuenta Acumulada *</label>
            <EntityAutocomplete id="fixedassetstab-cuenta-acumulada"
              options={imputableAccounts.map(a => ({ id: a.id, label: `${a.code} — ${a.name}` }))}
              value={accountAccumulated} onChange={setAccountAccumulated}
              placeholder="Seleccionar" allowClear />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label htmlFor="fixedassetstab-centro-de-costo" className="block text-sm font-medium text-content mb-1">Centro de Costo</label>
            <EntityAutocomplete id="fixedassetstab-centro-de-costo"
              options={costCenterOptions}
              value={costCenter} onChange={setCostCenter}
              placeholder="Opcional" allowClear />
          </div>
          <div>
            <label htmlFor="fixedassetstab-ubicacion" className="block text-sm font-medium text-content mb-1">Ubicacion</label>
            <input id="fixedassetstab-ubicacion" value={location} onChange={e => setLocation(e.target.value)} placeholder="Ubicacion fisica (opcional)"
              className="w-full px-3 py-2 text-sm border border-input-border rounded-lg bg-input-bg text-content focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary-200" />
          </div>
        </div>
        {error && (
          <div className="bg-danger-surface border border-danger-border rounded-lg px-3 py-2 text-sm text-danger-content">{error}</div>
        )}
      </div>
    </Modal>
  )
}

// ─── Book Value Bar ─────────────────────────────────────────────────────────

function BookValueBar({ asset }: { asset: FixedAssetItem }) {
  const cost = parseFloat(asset.acquisition_cost) || 1
  const book = parseFloat(asset.book_value) || 0
  const pct = Math.max(0, Math.min(100, (book / cost) * 100))

  let barColor = 'bg-success-solid'
  if (pct < 30) barColor = 'bg-danger-solid'
  else if (pct < 60) barColor = 'bg-warning-solid'

  return (
    <div className="flex items-center gap-2">
      <div className="w-16 h-1.5 bg-surface-muted rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${barColor}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs text-content-muted">{pct.toFixed(0)}%</span>
    </div>
  )
}

// ─── Main Tab ───────────────────────────────────────────────────────────────

export function FixedAssetsTab() {
  const [filterStatus, setFilterStatus] = useState('')
  const [search, setSearch] = useState('')
  const [modalOpen, setModalOpen] = useState(false)
  const [disposeAsset, setDisposeAsset] = useState<FixedAssetItem | null>(null)

  const params: Record<string, string> = {}
  if (filterStatus) params.status = filterStatus
  if (search) params.search = search

  const { data, isLoading } = useFixedAssets(params as Parameters<typeof useFixedAssets>[0])
  const { data: accountsData } = useAccounts({ is_active: 'true' })

  const assets = data?.assets ?? []
  const accounts = accountsData?.accounts ?? []

  const buildActions = (asset: FixedAssetItem): KebabMenuItem[] => [
    {
      label: 'Dar de baja',
      icon: <XCircle size={14} />,
      onClick: () => setDisposeAsset(asset),
      hidden: asset.status !== 'active',
      className: 'text-danger-content-soft hover:bg-danger-surface',
    },
  ]

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex gap-2 flex-wrap">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-content-subtle" />
            <input aria-label="Buscar activos"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Buscar activos..."
              className="border border-divider rounded-lg pl-9 pr-3 py-1.5 text-sm text-content focus:ring-2 focus:ring-primary w-56"
            />
          </div>
          <select aria-label="Todos los estados"
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
            className="border border-divider rounded-lg px-3 py-1.5 text-sm text-content focus:ring-2 focus:ring-primary"
          >
            <option value="">Todos los estados</option>
            <option value="active">Activo</option>
            <option value="disposed">Dado de Baja</option>
            <option value="fully_depreciated">Depreciado</option>
          </select>
        </div>
        <button
          onClick={() => setModalOpen(true)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-on-primary text-sm rounded-lg hover:bg-primary-hover"
        >
          <Plus size={14} />Nuevo Activo
        </button>
      </div>

      <div className="bg-glass backdrop-blur-glass rounded-card border border-divider overflow-x-auto shadow-card">
        {isLoading ? (
          <div className="flex items-center justify-center py-16 text-content-subtle">
            <Loader2 size={20} className="animate-spin mr-2" />Cargando...
          </div>
        ) : assets.length === 0 ? (
          <div className="text-center py-16">
            <Building2 size={32} className="mx-auto text-content-subtle mb-3" />
            <p className="text-content-muted text-sm font-medium">Sin activos fijos registrados</p>
            <button
              onClick={() => setModalOpen(true)}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 text-sm text-primary border border-primary-200 rounded-lg hover:bg-primary-50"
            >
              <Plus size={14} />Registrar primer activo
            </button>
          </div>
        ) : (
          <DataTable
            columns={[
              { key: 'code', header: 'Código', render: a => <span className="font-mono text-content">{a.code}</span> },
              {
                key: 'name', header: 'Nombre',
                render: a => <span className="block max-w-[200px] truncate" title={a.name}>{a.name}</span>,
              },
              {
                key: 'category', header: 'Categoria',
                render: a => (
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${CATEGORY_COLORS[a.category] ?? 'bg-surface-muted text-content-muted'}`}>
                    {CATEGORY_LABELS[a.category] ?? a.category_display ?? a.category}
                  </span>
                ),
              },
              {
                key: 'acquisition_cost', header: 'Costo', numeric: true,
                sortValue: a => Number(a.acquisition_cost),
                render: a => <span className="whitespace-nowrap">{fmtCLP(a.acquisition_cost)}</span>,
              },
              {
                key: 'total_depreciated', header: 'Deprec. Acum.', numeric: true,
                sortValue: a => Number(a.total_depreciated),
                render: a => <span className="whitespace-nowrap">{fmtCLP(a.total_depreciated)}</span>,
              },
              {
                key: 'book_value', header: 'Valor Libro', numeric: true,
                sortValue: a => Number(a.book_value),
                render: a => (
                  <div className="flex flex-col items-end gap-1">
                    <span className="font-semibold text-content whitespace-nowrap">{fmtCLP(a.book_value)}</span>
                    <BookValueBar asset={a} />
                  </div>
                ),
              },
              {
                key: 'status', header: 'Estado',
                render: a => (
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[a.status] ?? 'bg-surface-muted text-content-muted'}`}>
                    {STATUS_LABELS[a.status] ?? a.status_display ?? a.status}
                  </span>
                ),
              },
              {
                key: 'acciones', header: 'Acciones',
                render: a => <div className="text-right"><KebabMenu items={buildActions(a)} /></div>,
              },
            ]}
            rows={assets}
            rowKey={a => a.id}
            surface={false}
            minWidth={980}
            caption="Activos fijos"
          />
        )}
      </div>

      {modalOpen && (
        <CreateAssetForm
          accounts={accounts}
          onClose={() => setModalOpen(false)}
        />
      )}

      {disposeAsset && (
        <DisposeModal
          asset={disposeAsset}
          onClose={() => setDisposeAsset(null)}
        />
      )}
    </div>
  )
}
